/**
 * The Personalise door's live preview.
 *
 * The browser does NOT rebuild the case. The render for the chosen design, model and case type is
 * already the variant image on this page, so this draws only the monogram on top of it. That keeps
 * the theme free of the layer PNGs the catalogue renderer uses and, more importantly, avoids a
 * second implementation of that renderer in JavaScript that would have to track the Node one
 * forever (docs/superpowers/specs/2026-09-05-case-builder-design.md, section 5).
 *
 * The DEFAULT colour and the WEIGHT are NOT computed here. They are measured once per design by
 * scripts/set-monogram-config.js and stored in the metafield as legibility.fill/weight, so the
 * browser and the server cannot disagree about how busy the artwork is. This script never samples
 * the product image's pixels.
 *
 * The outline colour IS computed here (spec 8j), from whichever fill colour the customer has
 * chosen - not from the metafield's legibility.outline, which was measured against the artwork's
 * ground and goes wrong for most of the eight colours once the customer picks their own. See
 * outlineFor() below.
 *
 * This preview is an approximation good enough to choose by. The server re-renders the real artwork
 * from the design state at add-to-bag, and that is what prints.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-personalise]');
  if (!root) return;

  var config;
  try { config = JSON.parse(root.dataset.config); } catch (e) { return; }
  // Spec 8e: vertical position and size no longer come from a zone box - they come from a single
  // fixed baseline plus a font-size fraction chosen by the Size radio (below). The zone's x/w are
  // still what centres the lettering horizontally (see cx below), so config.zones[0] is not dead,
  // only its y/h are - every zone this store ships is horizontally centred anyway (x + w/2 = 0.5),
  // so a design with no zone at all still centres correctly with this fallback.
  var zone = (config.zones && config.zones[0]) || { x: 0.3, w: 0.4 };
  // Spec 8e: the baseline is a fraction of the print FACE height, carried on the root element
  // rather than in config.zones, because it is one constant shared by all four sizes, not
  // per-zone data.
  var baseline = parseFloat(root.dataset.baseline);
  if (!(baseline > 0)) baseline = 0.74;
  // Spec 8e: this is a SINGLE legibility decision applied at every size. The spec notes the real
  // answer can differ per size (a larger monogram covers a different area of the artwork, and at
  // least one design - faded-union-jack - actually flips fill between Small and the larger sizes).
  // Per-size legibility is scripts/set-monogram-config.js's job (config.sizes[size].fill/outline/
  // weight becoming real per spec 8e's table), not shipped in the config yet, so do not reimplement
  // that measurement here - once it lands, read it from config.sizes[chosenSizeKey] instead of this.
  var legibility = config.legibility || { fill: 'black', outline: 'white', outlineMm: 0.3, weight: 'normal' };

  var toggle = root.querySelector('[data-personalise-toggle]');
  var toggleLabel = toggle && document.querySelector('label[for="' + toggle.id + '"]');
  var textInput = root.querySelector('[data-personalise-text]');
  var ack = root.querySelector('[data-personalise-ack]');
  var errorEl = root.querySelector('[data-personalise-error]');
  var canvas = root.querySelector('[data-personalise-canvas]');
  if (!toggle || !textInput || !ack || !canvas) return;

  // Seeded from what the server rendered, so the state is valid before the customer touches the
  // variant picker. A null templateId here would fail contract validation at add-to-bag with
  // nothing on screen to explain why.
  var deviceIndex = parseInt(root.dataset.deviceIndex, 10);
  var caseTypeIndex = parseInt(root.dataset.caseTypeIndex, 10);
  var currentOptions = [];
  currentOptions[deviceIndex >= 0 ? deviceIndex : 0] = root.dataset.initialDevice || '';
  if (caseTypeIndex >= 0) currentOptions[caseTypeIndex] = root.dataset.initialCaseType || '';

  var slug = function (s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); };

  function chosen(name, fallback) {
    var el = root.querySelector('input[name="' + name + '"]:checked');
    return el ? el.value : fallback;
  }

  // Spec 8d: fonts are named for how they look (Classic, Script, Modern, Bold), not by the theme's
  // internal role names, and each radio carries the real CSS straight from Liquid so this file never
  // re-maps a font key to a stack of its own - that mapping already lives in exactly one place
  // (snippets/pdp-personalise.liquid) and duplicating it here is how the two would drift the first
  // time a font changed.
  function chosenFont() {
    var el = root.querySelector('input[name="personalise-font"]:checked');
    if (el && el.dataset.fontStack) {
      return { stack: el.dataset.fontStack, weight: el.dataset.fontWeight || '400' };
    }
    // No font radios were rendered at all - only happens when a design's config lists a single
    // font (snippets/pdp-personalise.liquid only renders the fieldset when monogram.fonts.size > 1).
    // There is nothing to read in that case, so fall back to the theme's own heading face.
    return { stack: "'Playfair Display', Georgia, serif", weight: '400' };
  }

  function chosenColour() {
    var el = root.querySelector('input[name="personalise-colour"]:checked');
    if (el) return el.value;
    return legibility.fill === 'white' ? '#ffffff' : '#111111';
  }

  // Spec 8j: the outline is derived from the customer's CHOSEN fill colour, not read from the
  // metafield. The metafield's outline was picked to fit the ARTWORK's ground, but the customer can
  // pick any of the eight colours independently of that ground - taking the metafield's outline
  // verbatim breaks five of the eight on a light-ground design (see spec 8j's table). Rec. 709 luma
  // on the fill's own RGB decides it instead: a light fill gets a dark outline and a dark fill gets
  // a light one, so the outline defines the letter shape when the ground matches the fill, and the
  // fill does when the ground matches the outline - a visible edge either way.
  function outlineFor(hex) {
    var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
    if (!m) return legibility.outline === 'white' ? '#ffffff' : '#111111';
    var r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
    var luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma >= 128 ? '#111111' : '#ffffff';
  }

  // Spec 8e: the four sizes (Small/Medium/Large/Statement) live as data-font-size on each radio in
  // snippets/pdp-personalise.liquid, not as a table here - that keeps the 0.10/0.17/0.26/0.36
  // fractions in exactly one place. Medium (0.17) is the fallback because it is also the config's
  // defaultSize, and snippets/pdp-personalise.liquid always renders the Size fieldset with one
  // radio checked, so this only matters if a design's config ever omitted the size fieldset.
  function chosenFontSize() {
    var el = root.querySelector('input[name="personalise-size"]:checked');
    var frac = el && parseFloat(el.dataset.fontSize);
    return frac > 0 ? frac : 0.17;
  }

  // Spec 8f: the line item properties a human reads at fulfilment must carry the LABEL a customer
  // recognises (Classic/Script/Modern/Bold, a colour name, a size name), not the internal key or a
  // hex value. Rather than keeping a second name table here that could drift from the one in
  // snippets/pdp-personalise.liquid, each of these reads the exact text already rendered next to the
  // checked radio - the font/size <span> label, or the colour swatch's own title attribute.
  function checkedRadio(name) {
    return root.querySelector('input[name="' + name + '"]:checked');
  }

  function chosenFontLabel() {
    var el = checkedRadio('personalise-font');
    var span = el && el.nextElementSibling;
    if (span && span.textContent && span.textContent.trim()) return span.textContent.trim();
    return 'Classic';
  }

  function chosenColourName() {
    var el = checkedRadio('personalise-colour');
    if (el && el.title) return el.title;
    return 'Black';
  }

  function chosenSizeLabel() {
    var el = checkedRadio('personalise-size');
    var span = el && el.nextElementSibling;
    if (span && span.textContent && span.textContent.trim()) return span.textContent.trim();
    return 'Medium';
  }

  // Spec 8f: the price comes from a SEPARATE "Personalised Case" product (Device x Case Type
  // variants, £39/£49) because a line item property cannot change price. root.dataset.personalisedHandle
  // and root.dataset.personalisedVariants are only rendered by snippets/pdp-personalise.liquid when
  // block.settings.personalised_product_handle resolves to a real product - blank today, since that
  // product does not exist yet. When blank, the checkbox itself is rendered `disabled` server-side
  // (see the Liquid), so toggle.checked can never become true and none of this runs in practice; the
  // checks below are the same guarantee enforced again on the JS side, in case that ever changes.
  var personalisedHandle = root.dataset.personalisedHandle || '';
  var personalisedVariants = [];
  try { personalisedVariants = JSON.parse(root.dataset.personalisedVariants || '[]'); } catch (e) { personalisedVariants = []; }
  var personalisedReady = !!(personalisedHandle && personalisedVariants.length);

  // Matched on the SAME Device and Case Type option VALUES the customer has selected on this
  // product - never by position or index, since the personalised product is free to declare its
  // options in a different order (snippets/pdp-personalise.liquid resolves p_device_index /
  // p_case_type_index against ITS OWN options for exactly this reason).
  function findPersonalisedVariant() {
    if (!personalisedReady) return null;
    var device = currentOptions[deviceIndex >= 0 ? deviceIndex : 0];
    var caseType = caseTypeIndex >= 0 ? currentOptions[caseTypeIndex] : null;
    for (var i = 0; i < personalisedVariants.length; i++) {
      var v = personalisedVariants[i];
      if (v.device === device && (caseType == null || v.caseType === caseType)) return v;
    }
    return null;
  }

  // The gallery image for the variant currently selected. This is the case render we draw on.
  //
  // snippets/product-images.liquid renders every medium as a `.product-main-slide` inside
  // `[data-product-images]` and leaves them all in the DOM; theme.js moves between them with
  // Flickity rather than swapping a single <img> src, and the currently visible one carries
  // `.is-selected` (added by Flickity itself, or by theme.js directly when there is only one
  // image and no slider is initialised - see `initProductSlider` in assets/theme.js). Scoping to
  // `[data-product-images]` keeps this from ever matching a slider in an unrelated section, such
  // as a related-products carousel.
  function galleryImage() {
    var gallery = document.querySelector('[data-product-images]');
    if (!gallery) return null;
    return gallery.querySelector('.product-main-slide.is-selected img')
        || gallery.querySelector('.product-main-slide.starting-slide img')
        || gallery.querySelector('.product-main-slide img');
  }

  // Where "the case" sits within the product photo, as fractions of the whole image.
  //
  // The zone in custom.monogram is expressed in fractions of the print FACE (the case's own visible
  // back), but the product photo is a square canvas with the case drawn smaller than the frame and
  // centred, plus a shadow margin - not the same rectangle, and treating them as one would put the
  // monogram in the wrong place on every model.
  //
  // packages/case-render/src/geometry.js renders every model's case at a FIXED height
  // (DEFAULT_CASE_HEIGHT_PX = 1861) on a FIXED square canvas (renderPreview's default `canvas` =
  // 2000), centred both ways. Only the case's WIDTH varies, by the phone's own aspect ratio - the
  // vertical framing is identical for all 32 models, so it isn't template data the browser is
  // missing, it's arithmetic:
  var CANVAS_PX = 2000;
  var CASE_HEIGHT_PX = 1861;
  var FACE_TOP_FRAC = (CANVAS_PX - CASE_HEIGHT_PX) / 2 / CANVAS_PX;  // ~0.0348
  var FACE_HEIGHT_FRAC = CASE_HEIGHT_PX / CANVAS_PX;                  // ~0.9305 (matches the "case
                                                                        // fills 93% of the frame
                                                                        // height" comment there)
  // Width does vary by model (measured across the 32-model catalogue: case aspect ratio 0.486-0.512,
  // median ~0.496) and the theme carries no per-model geometry - spec 5 is explicit that this door
  // ships zero new theme assets, so there is no template.json to read in the browser. Every zone
  // this store has shipped is horizontally centred (x + w/2 = 0.5), and the case itself is always
  // centred on the canvas, so the monogram's CENTRE lands correctly regardless of which model's
  // aspect ratio is in play; only a zone's pixel WIDTH would need this constant, and nothing below
  // uses width for anything but centring.
  var CASE_ASPECT = 0.496;
  var FACE_WIDTH_FRAC = FACE_HEIGHT_FRAC * CASE_ASPECT;
  var FACE_LEFT_FRAC = (1 - FACE_WIDTH_FRAC) / 2;

  function draw() {
    var img = galleryImage();
    if (!img) return;
    // Lazy-loaded slides (assets/product-images.liquid sets loading="lazy" on everything but the
    // opening slide) may not have finished loading when a variant change brings them into view, so
    // draw once it has rather than measuring a zero-size image.
    //
    // Attached unconditionally and WITHOUT `once`. A one-shot listener fires for the first image and
    // never again, so any later swap of this element's `src` leaves the canvas sized and positioned
    // for the PREVIOUS image. That is exactly the glitch the photo case showed, and the only reason
    // it never showed here is that the catalogue reuses one iPhone 16 Pro image for every variant.
    //
    // Safe on every draw: addEventListener de-duplicates an identical type + function + capture
    // triple, so this never stacks listeners.
    img.addEventListener('load', draw);
    if (!img.complete || !img.naturalWidth) return;

    var rect = img.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    // Size AND position the canvas from the image's own viewport rect. The CSS gives the canvas
    // position: fixed with top/left at 0, so without this it renders in the page corner rather than
    // over the case - getBoundingClientRect() already returns viewport coordinates, which is exactly
    // what position: fixed consumes, so this needs no positioned ancestor.
    canvas.width = Math.round(rect.width);
    canvas.height = Math.round(rect.height);
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';
    canvas.style.top = rect.top + 'px';
    canvas.style.left = rect.left + 'px';

    var ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    var value = (textInput.value || '').trim();
    if (!value) return;

    // Horizontal placement is unchanged (spec 8e): the zone's x/w still centre the lettering.
    var cx = (FACE_LEFT_FRAC + (zone.x + zone.w / 2) * FACE_WIDTH_FRAC) * canvas.width;

    // Spec 8e: ONE baseline, not a box. The root's data-baseline is a fraction of the print FACE
    // height, exactly like the old zone.y was, so it goes through the same FACE_TOP_FRAC /
    // FACE_HEIGHT_FRAC conversion as everything else on this canvas.
    var faceHeightPx = FACE_HEIGHT_FRAC * canvas.height;
    var baselineY = (FACE_TOP_FRAC + baseline * FACE_HEIGHT_FRAC) * canvas.height;

    var font = chosenFont();
    // Spec 8b step 3: at ambiguity >= 0.35 the ladder forces a heavier weight regardless of which
    // font the customer picked, because that is what carries the hostile 8% of the catalogue -
    // this overrides the font's own natural weight rather than replacing it outright, so Classic
    // still reads as Classic everywhere else.
    var weight = legibility.weight === 'bold' ? '700' : font.weight;
    // Spec 8e: font size is the chosen size's fraction of the FACE height in canvas pixels, not a
    // fraction of a zone box - this is what makes the font grow upward from a fixed baseline
    // instead of scaling within a box whose own centre used to move between sizes.
    var fontSizePx = chosenFontSize() * faceHeightPx;
    ctx.font = weight + ' ' + Math.round(fontSizePx) + 'px ' + font.stack;

    // The nominal fraction above accounts for HEIGHT only, nothing bounds WIDTH, so at Statement
    // size three wide capitals ("APP") can render past the edge of the case. That is worse than
    // looking wrong: the print wraps around the case sides (an iPhone 17 Air's visible back is
    // 76.9mm but the full printed wrap is 105.1mm - 14.1mm each side is never seen flat), so text
    // that runs off the visible back does not just look bad, it disappears round the edge.
    //
    // The cap is 0.80 of the face WIDTH, leaving about 10% margin each side (roughly 7.7mm on a 17
    // Air) comfortably inside the visible back. Tighten letter-spacing first - a small negative
    // value reads as deliberate tracking, not distortion - and only reduce the font size if that
    // alone is not enough. Never scale the text horizontally with a transform: that would squash
    // glyph proportions, give the strokes the wrong weight, and read as cheap rather than premium.
    //
    // This can only ever SHRINK fontSizePx below the nominal value spec 8e's clearance table was
    // measured against, never grow it past that value - so the vertical clearance above the camera
    // that table guarantees at the nominal size can only improve here, never be put at risk. That is
    // the "cap vertically" guard: it falls out of this only ever reducing size, not a second
    // measurement against camera geometry the browser does not have (spec 5 ships no per-model data
    // to the theme).
    var maxTextWidth = FACE_WIDTH_FRAC * canvas.width * 0.80;
    var supportsLetterSpacing = 'letterSpacing' in ctx;
    if (supportsLetterSpacing) ctx.letterSpacing = '0px';
    var measuredWidth = ctx.measureText(value).width;
    if (measuredWidth > maxTextWidth) {
      if (supportsLetterSpacing) {
        ctx.letterSpacing = '-0.02em';
        measuredWidth = ctx.measureText(value).width;
      }
      if (measuredWidth > maxTextWidth && measuredWidth > 0) {
        fontSizePx = Math.max(1, fontSizePx * (maxTextWidth / measuredWidth));
        ctx.font = weight + ' ' + Math.round(fontSizePx) + 'px ' + font.stack;
      }
    }

    ctx.textAlign = 'center';
    // Spec 8e: alphabetic, not middle - the bottom of the letters (the baseline) is the fixed
    // point; only the font size above changes between the four sizes.
    ctx.textBaseline = 'alphabetic';

    // The outline is ours and not optional (spec 8b): it is what makes the lettering legible on the
    // designs where a plain fill would half-disappear, and it costs nothing where contrast is good.
    // 0.3mm at print scale, expressed here as a fraction of the case height. The THICKNESS still
    // comes from the metafield (legibility.outlineMm) - only the colour is derived from the
    // customer's own choice (spec 8j), via outlineFor() below.
    var fillColour = chosenColour();
    ctx.lineWidth = Math.max(1, canvas.height * (legibility.outlineMm / 150));
    ctx.strokeStyle = outlineFor(fillColour);
    ctx.lineJoin = 'round';
    ctx.strokeText(value, cx, baselineY);

    ctx.fillStyle = fillColour;
    ctx.fillText(value, cx, baselineY);
  }

  // Add to bag is blocked until the customer has both entered something and ticked the
  // acknowledgement (spec 8c). Disabling the real button is clearer than failing on submit.
  function gate() {
    var value = (textInput.value || '').trim();
    var ready = !toggle.checked || (value.length > 0 && ack.checked);
    var button = document.querySelector('[data-product-blocks] [data-add-to-cart]');
    if (button) button.disabled = !ready;
    if (errorEl) {
      var msg = '';
      if (toggle.checked && !value) msg = 'Add your initials, or untick to buy the plain case.';
      else if (toggle.checked && !ack.checked) msg = 'Please confirm your spelling before adding to bag.';
      errorEl.textContent = msg;
      errorEl.hidden = !msg;
    }
  }

  function syncExpanded() {
    // On the checkbox, not the label: a label's role does not support aria-expanded.
    if (toggle) toggle.setAttribute('aria-expanded', toggle.checked ? 'true' : 'false');
  }

  // Spec 8f: "the displayed price must update the moment personalisation is turned on, not at the
  // cart." [data-product-price] is theme.js's own selector for the PDP price element (see
  // `selectors.price` and `this.cache.price` in assets/theme.js, which sets its innerHTML on every
  // variant change via theme.Currency.formatMoney) - reusing it rather than inventing a new hook
  // means this reads the exact element the rest of the page already treats as authoritative.
  var priceEl = document.querySelector('[data-product-price]');
  // The plain price for whatever variant is currently selected. Captured fresh (not just once at
  // load) so that closing the panel after a variant change restores the CORRECT plain price rather
  // than a stale one from whenever the page first loaded.
  var basePriceHTML = priceEl ? priceEl.innerHTML : null;

  function formatMoney(cents) {
    // Prefer the theme's own formatter and money_format setting (window.theme, set up in
    // layout/theme.liquid) so this matches whatever format the merchant has configured, exactly
    // like theme.js's own price updates do. Falls back to a plain £ format if that global is not
    // present for any reason - this store's currency is always GBP.
    if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === 'function') {
      var fmt = (window.theme.settings && window.theme.settings.moneyFormat) || '£{{amount}}';
      return window.theme.Currency.formatMoney(cents, fmt);
    }
    return '£' + (cents / 100).toFixed(2);
  }

  function updatePriceDisplay() {
    if (!priceEl) return;
    if (toggle.checked) {
      var variant = findPersonalisedVariant();
      if (variant && typeof variant.price === 'number') {
        priceEl.innerHTML = formatMoney(variant.price);
        return;
      }
    }
    if (basePriceHTML != null) priceEl.innerHTML = basePriceHTML;
  }

  // Spec, this session: the button used to run the full width of the block, overshooting the
  // Device/Case Type dropdowns beside it by a long way. Those dropdowns (.variant-wrapper--dropdown
  // in snippets/variant-dropdown.liquid) are plain <select> elements with no fixed width, flex-basis
  // or grid column in this theme's CSS (assets/theme.css.liquid: display:inline-block, max-width:
  // 100%, sized to content) - their combined width changes with the selected device name ("iPhone
  // 12" vs "iPhone 17 Pro Max"), so it has to be measured, not hardcoded. This reads the real span
  // from the first dropdown's left edge to the last one's right edge and sets it as an inline width,
  // overriding the CSS max-width fallback in snippets/pdp-personalise.liquid.
  var blocksContainer = root.closest('[data-product-blocks]');

  function syncToggleWidth() {
    if (!toggleLabel || !blocksContainer) return;
    var wrappers = blocksContainer.querySelectorAll('[data-dynamic-variants-enabled] .variant-wrapper--dropdown');
    if (!wrappers.length) return;
    var first = wrappers[0].getBoundingClientRect();
    var last = wrappers[wrappers.length - 1].getBoundingClientRect();
    var width = last.right - first.left;
    if (!(width > 0)) return;
    toggleLabel.style.maxWidth = 'none';
    toggleLabel.style.width = Math.round(width) + 'px';
  }

  function refresh() { draw(); gate(); syncExpanded(); updatePriceDisplay(); syncToggleWidth(); }

  // ── Measurement (GA4) ─────────────────────────────────────────────────────────────────────
  //
  // How many people open the panel, against how many actually buy a personalised case. gtag is
  // injected by Shopify's Google & YouTube channel rather than by this theme, so it may legitimately
  // be absent — an ad blocker, or the channel being disconnected — and analytics must never be able
  // to break add-to-bag. Hence the typeof guard and the try/catch.
  function track(event, params) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', event, params || {});
    } catch (e) { /* measurement is never worth an exception on the buying path */ }
  }

  function trackingParams() {
    return {
      design: root.dataset.productTitle || root.dataset.productHandle || '',
      design_handle: root.dataset.productHandle || '',
      device: currentOptions[deviceIndex >= 0 ? deviceIndex : 0] || '',
      case_type: caseTypeIndex >= 0 ? (currentOptions[caseTypeIndex] || '') : ''
    };
  }

  // The panel's own open/close is CSS-only (a checked sibling selector on .personalise__toggle-input,
  // see snippets/pdp-personalise.liquid), on purpose, so it still works with JavaScript off - this
  // never sets `hidden` on the editor or the canvas itself. The canvas needs no visibility rule of
  // its own either: it is a descendant of .personalise__editor, and a display:none ancestor never
  // renders its descendants, so it is already hidden until the panel opens.
  toggle.addEventListener('change', refresh);

  // Fired only on OPEN, not on close, so the count is "people who considered personalising" rather
  // than a tally of toggling. That is the top of the funnel whose bottom is personalise_add_to_cart
  // and, ultimately, a Personalised Case purchase in GA4's own ecommerce reports.
  toggle.addEventListener('change', function () {
    if (toggle.checked) track('personalise_open', trackingParams());
  });
  textInput.addEventListener('input', refresh);
  ack.addEventListener('change', gate);
  root.addEventListener('change', function (e) {
    if (e.target.name === 'personalise-font' || e.target.name === 'personalise-colour' || e.target.name === 'personalise-size') draw();
  });

  // The model or case type changed, so the gallery image is ABOUT to change under us - variant:change
  // is dispatched synchronously before theme.js even calls Flickity's goToSlide (see _onSelectChange
  // in assets/theme.js: it fires this event, then _updateImages, then the image swap), so there is no
  // fixed number of frames to wait. Watching the gallery for the actual DOM change that marks the new
  // slide as visible is what the swap really is, so that is what this waits for - a MutationObserver
  // on the slide classes catches Flickity moving `is-selected`, and draw() itself waits on the new
  // image's `load` event if it has not finished loading (see draw() above).
  document.addEventListener('variant:change', function (e) {
    var v = e.detail && e.detail.variant;
    if (v && v.options) currentOptions = v.options;
    draw();
    syncToggleWidth();
    // theme.js dispatches 'variant:change' and THEN calls its own _updatePrice(), which writes the
    // plain price into priceEl synchronously, straight after this listener returns (see
    // Product.prototype._onSelectChange in assets/theme.js). Calling updatePriceDisplay() here
    // directly would just get overwritten a moment later. Deferring to the next tick lets that
    // write happen first, so this both wins the race for the personalised override AND, while the
    // panel is closed, recaptures the fresh plain price as the new basePriceHTML - the correct
    // "restore to" value for whichever variant is selected now, not a stale one from page load.
    setTimeout(function () {
      if (!toggle.checked && priceEl) basePriceHTML = priceEl.innerHTML;
      updatePriceDisplay();
    }, 0);
  });

  var galleryEl = document.querySelector('[data-product-images]');
  if (galleryEl && window.MutationObserver) {
    // 'src' and 'srcset' as well as 'class': Flickity moving `is-selected` is only one of the two
    // ways the visible image changes. The theme also swaps the src on the existing element for a
    // variant image, which changes no classes at all. The catalogue reuses one iPhone 16 Pro image
    // across every variant, so that path never fires here today - but it is the same latent bug that
    // WAS visible on the photo case, and it would appear the day designs get per-model imagery.
    new MutationObserver(draw).observe(galleryEl, {
      attributes: true,
      attributeFilter: ['class', 'src', 'srcset'],
      subtree: true
    });
  }

  window.addEventListener('resize', draw);
  window.addEventListener('resize', syncToggleWidth);
  window.addEventListener('scroll', draw, { passive: true });

  // Spec 8f: "the block renders OUTSIDE the add-to-cart form". Measured on the live page: the form
  // (rendered by snippets/product-form.liquid's {% form 'product' %} tag) ends at character 158,617,
  // and this block starts at 267,387 - it is a later, separate sibling under [data-product-blocks],
  // not a descendant of the form. Its properties[...] inputs (and, before this fix, the initials box
  // itself) therefore could never submit as line item properties, whatever they were named: a real
  // personalised order would have arrived with no initials on it and nothing to explain why.
  //
  // Moving the block inside the form was rejected as too invasive (product-template.liquid's block
  // loop is shared by all 212 products). Instead, this copies the panel's values into the REAL form
  // as hidden inputs immediately before it submits.
  //
  // form[action*="/cart/add"] is what Shopify's {% form 'product' %} tag always renders, and is the
  // one thing that reliably distinguishes it from every other form on a product page: the predictive
  // search form posts to routes.search_url ("/search"), the cart drawer/cart page forms post to
  // routes.cart_url ("/cart"), and the newsletter/contact forms use Shopify's {% form 'customer' %}
  // / {% form 'contact' %} tags, neither of which touches /cart/add. It is also the exact selector
  // ('.product-single__form') theme.js itself resolves as `selectors.form` for its own Product
  // component (assets/theme.js), confirming this is the form the rest of the theme already treats as
  // canonical, not a guess.
  var cartForm = document.querySelector('form[action*="/cart/add"]');

  if (cartForm) {
    // ── Handing the bag over to the builder service ────────────────────────────────────────
    //
    // Four earlier attempts failed on the live page. Keeping the reasons, because each looked
    // correct in review and only the live page disproved it:
    //
    //   1. A 'submit' listener with preventDefault(). preventDefault cancels the BROWSER's native
    //      submission and nothing else, so theme.js's own submit handler still ran and added the
    //      plain product.
    //   2. The same, plus stopImmediatePropagation(), to stop that handler. It did not.
    //   3. Swapping the variant id in the DOM field and restoring it on a setTimeout, on the
    //      assumption the form is read within the same dispatch.
    //   4. The 'product-form:cart:request' hook, which snippets/form.product.liquid documents as
    //      cancellable for exactly this purpose. That snippet defines a <product-form> custom
    //      element which IS NOT USED on this PDP: the page has no <product-form> element and never
    //      defines the custom element. It is dead code, and the hook never fired.
    //
    // What actually runs is theme.js's AjaxProduct, which binds to the form's submit and builds
    // `new FormData(this.form)` synchronously inside it. Every failure above put the same dangerous
    // thing in the bag: a £35 line carrying personalisation properties, an order that looks
    // personalised, is not priced for it, and would be discovered at the printer rather than here.
    var SAVE_FAILED_MESSAGE = "We couldn't save your design, please try again";

    // Measured render times on the server: 7.6s for a medium monogram, 13.5s for a statement one
    // (the case render is a per-pixel JS loop, and a bigger monogram is a bigger composite). 20s
    // clears the slowest measured case with room to spare.
    //
    // Spec 9.1 asks for "15 seconds with one retry". 15s is under the measured worst case, so it
    // would time out every statement-size render and then retry into the same timeout, making the
    // customer wait 30s to be told no. So this is ONE attempt at 20s, and the retry is the customer
    // clicking again - which is what the spec's own customer-facing wording describes anyway.
    var BUILDER_TIMEOUT_MS = 20000;

    function builderUrl() {
      var base = (root.dataset.builderUrl || '').replace(/\/+$/, '');
      return base ? base + '/designs' : null;
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = !message;
    }

    // A 13 second wait with a silent button reads as a broken page, so say what is happening.
    var labelEl = document.querySelector('[data-product-blocks] [data-add-to-cart-text]');
    var savedLabel = null;
    function setSaving(on) {
      if (!labelEl) return;
      if (on) {
        if (savedLabel === null) savedLabel = labelEl.innerHTML;
        labelEl.textContent = 'Saving your design...';
      } else if (savedLabel !== null) {
        labelEl.innerHTML = savedLabel;
        savedLabel = null;
      }
    }

    // fetch + AbortController rather than a bare fetch: without the abort a hung connection would
    // leave the customer watching a dead button with no way back.
    function postDesign(state) {
      var url = builderUrl();
      if (!url || typeof fetch !== 'function') return Promise.reject(new Error(SAVE_FAILED_MESSAGE));

      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = setTimeout(function () { if (controller) controller.abort(); }, BUILDER_TIMEOUT_MS);

      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(state),
        signal: controller ? controller.signal : undefined
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (body) {
          // A 4xx is something the customer can act on (a blank field, an unsupported combination),
          // so its message is shown as sent. A 5xx is ours, and the generic line is the honest
          // thing to say about it.
          if (!res.ok) {
            var detail = body && body.errors && body.errors.length ? body.errors[0] : null;
            throw new Error(res.status < 500 && detail ? detail : SAVE_FAILED_MESSAGE);
          }
          if (!body || !body.designId) throw new Error(SAVE_FAILED_MESSAGE);
          return body;
        });
      }).then(function (body) { clearTimeout(timer); return body; },
              function (err) { clearTimeout(timer); throw err; });
    }

    // Rather than mutating the theme's form and letting it post, the personalised line is added
    // with its own clean request. The form cannot be reused as-is: it carries `product-id` for the
    // DESIGN being viewed, while the variant being added belongs to the separate Personalised Case
    // product, and Shopify rejects that mismatch with a 400. Patching the form to agree would mean
    // rewriting product-id and section-id too, then restoring all three, for no gain.
    function addPersonalisedLine(variant, saved) {
      var quantityField = cartForm.querySelector('[name="quantity"]');
      var quantity = quantityField ? parseInt(quantityField.value, 10) : 1;

      var payload = {
        id: variant.id,
        quantity: quantity > 0 ? quantity : 1,
        properties: {
          // Visible to the customer in the bag and on the order.
          Personalisation: (textInput.value || '').trim(),
          Style: chosenFontLabel(),
          Colour: chosenColourName(),
          Size: chosenSizeLabel(),
          // The readable title, not the handle: this line is shown to the customer.
          Design: root.dataset.productTitle || root.dataset.productHandle || '',
          // Underscore-prefixed: hidden from the customer, visible in the admin and on the order.
          // `_preview` is what the cart template and the confirmation email show as the line image.
          // Spec 9.3 also lists `_print_file`; it is deliberately absent, because the response
          // contract never hands full-resolution artwork to a browser. Fulfilment reads it from the
          // KKOps print queue, keyed by `_design_id`.
          _design_id: saved.designId,
          _preview: saved.thumbUrl || saved.previewUrl || ''
        }
      };

      var cartAdd = (window.theme && theme.routes && theme.routes.cartAdd) ||
        ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart/add.js';

      return fetch(cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (json) {
          if (!res.ok) throw new Error(json && json.description ? json.description : SAVE_FAILED_MESSAGE);
          return json;
        });
      });
    }

    // Mirrors theme.js AjaxProduct's own success handling, so a personalised add behaves exactly
    // like a plain one: this store is cartType 'page', so it goes to the cart; a drawer store gets
    // the event its drawer listens for.
    function afterAdded(line) {
      // The middle of the funnel. `value` and `currency` are included so this can be used as a
      // conversion in GA4 without any further configuration.
      track('personalise_add_to_cart', Object.assign(trackingParams(), {
        value: line && typeof line.price === 'number' ? line.price / 100 : undefined,
        currency: 'GBP',
        initials_length: (textInput.value || '').trim().length,
        style: chosenFontLabel(),
        colour: chosenColourName(),
        size: chosenSizeLabel()
      }));

      cartForm.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: { product: line, addToCartBtn: addButton },
        bubbles: true
      }));
      if (window.theme && theme.settings && theme.settings.cartType === 'page') {
        window.location = theme.routes.cartPage;
      }
    }

    // ── The interception point: the CLICK, not the submit ──────────────────────────────────
    //
    // theme.js's AjaxProduct binds to the form's submit and builds `new FormData(this.form)`
    // synchronously inside it, so no submit listener of ours can get in front of it reliably.
    // Cancelling the click is deterministic: the button is type="submit", so a cancelled click
    // fires no submit event at all and the theme's handler never runs.
    //
    // Capture phase on document, so load order cannot decide who wins.
    var addButton = document.querySelector('[data-product-blocks] [data-add-to-cart]');

    document.addEventListener('click', function (e) {
      // Personalisation off: an ordinary case at the ordinary price, handled by the theme as always.
      if (!toggle.checked) return;
      if (!addButton || !e.target || !addButton.contains(e.target)) return;

      // From here the add is ours. Cancel first and unconditionally: any path that fell through
      // would let a personalised order into the bag at the plain case's price.
      e.preventDefault();
      e.stopImmediatePropagation();

      if (!personalisedReady) {
        return showError('Personalisation is not available for this design yet, please untick to buy the plain case.');
      }
      var variant = findPersonalisedVariant();
      if (!variant) {
        return showError('Personalisation is not available for this combination, please try a different device or case type.');
      }
      if (!(textInput.value || '').trim()) return showError('Add your initials, or untick to buy the plain case.');
      if (!ack.checked) return showError('Please confirm your spelling before adding to bag.');

      showError('');
      setSaving(true);

      postDesign(designState())
        .then(function (saved) { return addPersonalisedLine(variant, saved); })
        .then(function (line) { setSaving(false); afterAdded(line); })
        .catch(function (err) {
          setSaving(false);
          showError((err && err.message) || SAVE_FAILED_MESSAGE);
        });
    }, true);
  }

  // Shaped exactly like EXAMPLE_PERSONALISE_STATE in scripts/lib/builder-contract.js. A function
  // declaration, so the submit handler above can call it despite being written earlier in the file.
  function designState() {
      return {
        version: 1,
        // Read by option POSITION resolved server-side, never by assuming option1 is the Device.
        templateId: slug(currentOptions[deviceIndex >= 0 ? deviceIndex : 0]),
        caseType: /tough/i.test(String(caseTypeIndex >= 0 ? currentOptions[caseTypeIndex] : '')) ? 'tough' : 'hard',
        mode: 'personalise',
        base: { kind: 'design', designHandle: root.dataset.productHandle },
        text: {
          value: (textInput.value || '').trim(),
          font: chosen('personalise-font', 'classic'),
          colour: chosenColour(),
          // The size the customer actually picked. Without it the service falls back to the
          // design's default size, so a customer who chose Statement would be sent Medium.
          size: chosen('personalise-size', 'medium'),
          zone: zone.id || 'monogram'
        },
        acknowledged: ack.checked ? [ack.value] : []
      };
  }

  window.KKPersonalise = {
    active: function () { return toggle.checked; },
    state: designState
  };

  syncExpanded();
  refresh();
})();
